import { ZRoomValidationSummary } from "./roomValidationSchema";
import { ZRoomValidationDiffItem } from "./roomValidationSchema";
import { z } from "zod";
import { ZArticleValidationSettings } from "../projectBase.schema";
/**
 * Artikkelvalidering: spesifikasjon (RFP-kilde) vs modell.
 *
 * Gjenbruker eksisterende i schemas.ts:
 * - ZValidationStatus
 * - ZRoomValidationSummary
 * - ZRoomValidationDiffItem
 * - ZRoomValidationSources
 *
 * Ingen "roomRef" her – valideringen ligger på Room-dokumentet og rominfo finnes allerede der.
 */
/* -------------------------------------------------------------------------------------------------
 * Optional: mengde-KPI (ikke dupliserer rominfo – kun summer)
 * ------------------------------------------------------------------------------------------------- */
export const ZRoomValidationArticleQuantities = z.object({
    /** Sum av forventet mengde (spec), etter aggregering per compCode. */
    expectedQuantityTotal: z.number().int().nonnegative(),
    /** Sum av observert mengde (modell), etter aggregering per compCode. */
    observedQuantityTotal: z.number().int().nonnegative(),
    /** observed - expected */
    quantityDeltaTotal: z.number().int(),
});
/** Excluded comparison rows retain their real quantities for display/audit. */
export const ZIgnoredArticleValidationDiffItem = ZRoomValidationDiffItem.extend({
    reason: z.literal("PROJECT_IGNORED_COMPCODE"),
});
const ZRoomValidationSpecifiedArticlesBreakdown = z.object({
    summary: ZRoomValidationSummary,
    quantities: ZRoomValidationArticleQuantities.optional(),
    comparison: ZRoomValidationDiffItem.array().optional().default([]),
    /** Excluded from summary and quantities; absent on older snapshots. */
    ignoredComparison: z.array(ZIgnoredArticleValidationDiffItem).optional(),
});
/* -------------------------------------------------------------------------------------------------
 * Snapshot: specifiedArticles vs modell
 * ------------------------------------------------------------------------------------------------- */
/**
 * Diff-rader:
 * Vi gjenbruker ZRoomValidationDiffItem for maksimal deling av frontend-komponenter.
 *
 * Semantikk i denne valideringen:
 * - standardCount = forventet (spec)
 * - instanceCount = observert (modell)
 * - delta         = instanceCount - standardCount
 */
export const ZRoomValidationSpecifiedArticles = z.object({
    /** KPI-er (samme schema som standardrom-validering) */
    summary: ZRoomValidationSummary,
    /** Valgfri totalsummer for mengder (nyttig i UI, men valgfritt) */
    quantities: ZRoomValidationArticleQuantities.optional(),
    /** Per-compCode diff (samme schema som standardrom-validering) */
    comparison: ZRoomValidationDiffItem.array().optional().default([]),
    /** Excluded from summary and quantities; absent on older snapshots. */
    ignoredComparison: z.array(ZIgnoredArticleValidationDiffItem).optional(),
    /** Settings actually used for this run; absent means legacy/unknown. */
    appliedSettings: ZArticleValidationSettings.optional(),
    /** Kilder (spec + model) */
    /** Versjonering/fingerprint (samme felt som standardrom-validering) */
    engineVersion: z.string().min(1).optional(),
    sourceFingerprint: z.string().min(1).optional().nullable(),
    /** Produksjonstidspunkt */
    validatedAt: z.string().datetime(),
    /** Hvem/hva som produserte snapshot */
    validatedBy: z.string().min(1).default("system:specifiedarticles"),
    /** Valgfri note */
    note: z.string().optional().nullable(),
    /**
     * Framtid: per responsibility/discipline.
     * Nøkler anbefales normalisert (trim + uppercase) i motoren.
     *
     * Inneholder samme schema som "top-level", slik at UI-komponenter kan gjenbrukes.
     */
    perResponsibility: z
        .record(z.string().min(1), ZRoomValidationSpecifiedArticlesBreakdown)
        .optional(),
    /**
     * Per dRofus-list/listetype, basert på `specifiedArticles[].category`.
     * Eksempler: "Utstyr i rom", "Bygningsdeler", "RIE", "RIV".
     */
    perListType: z
        .record(z.string().min(1), ZRoomValidationSpecifiedArticlesBreakdown)
        .optional(),
});
